import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import Loader from "./Loader";
import { displayToast } from "../utils/displayToast";
import { useErrorHandler } from "../utils/ErrorHandler";
import { useOutsideClick } from "../utils/useOutsideClick";
import { fetchTransactions } from "../apis/transactionApi";
import {
  activateProjection,
  createProjection,
  deactivateProjection,
  deleteProjection,
  fetchProjectionOptions,
  fetchProjections,
  getProjectionErrorMessage,
  isProjectionError,
  updateProjection,
} from "../apis/fieldMasterProjections";
import {
  COPY_ONE_TARGET_MAX_LENGTH,
  FIELD_NAME_MAX_LENGTH,
  FIELD_NAME_PATTERN,
  FIELD_TITLE_MAX_LENGTH,
  NUMERIC_TARGET_MAX_LENGTH,
  PROJECTION_DATA_TYPE_LABELS,
  PROJECTION_FIELD_TYPES,
  PROJECTION_OPERATIONS,
  PROJECTION_OPERATION_ORDER,
} from "../constants/FieldMasterProjectionConstants";
import "../styles/components/PageStyles.css";
import "../styles/components/FieldMasterProjections.css";

const INTEGRAL_TYPES = new Set(["int", "bigint", "smallint", "mediumint"]);
const NUMERIC_TYPES = new Set([
  ...INTEGRAL_TYPES,
  "decimal",
  "float",
  "double",
]);
const EMPTY_LIST = [];

const EMPTY_NEW_FIELD = {
  field_name: "",
  title: "",
  data_type: "",
  field_type: "",
  field_length: "",
};

const EMPTY_FORM = {
  operation: "",
  sourceFieldId: "",
  targetMode: "existing",
  targetFieldId: "",
  newField: EMPTY_NEW_FIELD,
  showInHeadView: true,
};

const normalizeType = (value) =>
  String(value || "")
    .trim()
    .toLowerCase();

const isIntegralType = (value) => INTEGRAL_TYPES.has(normalizeType(value));
const isNumericType = (value) => NUMERIC_TYPES.has(normalizeType(value));

const isCompatibleSumTarget = (sourceType, targetType) => {
  if (!isNumericType(sourceType) || !isNumericType(targetType)) return false;
  return isIntegralType(sourceType) || !isIntegralType(targetType);
};

const getOperationMeta = (operation, serverOperations = []) => {
  const base = PROJECTION_OPERATIONS[operation];
  const server = serverOperations.find((item) => item.value === operation);
  if (!base && !server) return null;
  return {
    ...(base || {}),
    value: operation,
    label: server?.label || base?.label || operation,
    description: server?.description || base?.description || "",
    requiresSource:
      server && typeof server.requires_source === "boolean"
        ? server.requires_source
        : Boolean(base?.requiresSource),
  };
};

const formatFieldType = (field) => {
  if (!field) return "";
  const type = String(field.data_type || "").toLowerCase();
  return field.field_length ? `${type}(${field.field_length})` : type;
};

const buildPreview = ({ operation, source, targetName }) => {
  const target = targetName || "head_field";
  if (operation === "SUM") {
    return `${target} = SUM(${source?.field_name || "item_field"}) across all item rows`;
  }
  if (operation === "COUNT_ROWS") {
    return `${target} = COUNT_ROWS(item rows) for the transaction`;
  }
  if (operation === "COPY_ONE") {
    return `${target} = COPY_ONE(${source?.field_name || "item_field"}) from the first item ordered by serial_no, id`;
  }
  return "Select an operation to preview the projection.";
};

const formatMappingFormula = (row) => {
  const operation = row?.mapping?.operation;
  const sourceName = row?.source_item_field?.field_name;
  if (operation === "COUNT_ROWS") return "COUNT_ROWS(item rows)";
  if (operation === "SUM") return `SUM(${sourceName || "?"})`;
  if (operation === "COPY_ONE") return `COPY_ONE(${sourceName || "?"})`;
  return operation || "-";
};

const SearchableSelect = ({
  options,
  value,
  onChange,
  placeholder,
  disabled,
  emptyText = "No options",
  searchable = true,
}) => {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const wrapperRef = useOutsideClick(() => setOpen(false));
  const selected = options.find((option) => option.value === value);

  const visibleOptions = useMemo(() => {
    const needle = query.trim().toLowerCase();
    if (!needle) return options;
    return options.filter((option) =>
      `${option.label} ${option.meta || ""} ${option.keywords || ""}`
        .toLowerCase()
        .includes(needle),
    );
  }, [options, query]);

  const toggle = () => {
    if (disabled) return;
    setOpen((prev) => !prev);
    setQuery("");
  };

  const select = (option) => {
    if (option.disabled) return;
    onChange(option.value);
    setOpen(false);
    setQuery("");
  };

  return (
    <div
      className={`fmp-select ${open ? "is-open" : ""} ${disabled ? "is-disabled" : ""}`}
      ref={wrapperRef}
    >
      <button
        type="button"
        className="fmp-select-toggle"
        onClick={toggle}
        disabled={disabled}
        aria-haspopup="listbox"
        aria-expanded={open}
      >
        <span
          className={`fmp-select-value ${selected ? "" : "is-placeholder"}`}
        >
          {selected ? (
            <>
              <span className="fmp-select-label">{selected.label}</span>
              {selected.meta ? (
                <span className="fmp-select-meta">{selected.meta}</span>
              ) : null}
            </>
          ) : (
            placeholder
          )}
        </span>
        <span className="fmp-select-caret" aria-hidden="true" />
      </button>
      {open && (
        <div className="fmp-select-menu" role="listbox">
          {searchable && (
            <input
              autoFocus
              type="text"
              className="fmp-select-search"
              placeholder="Type to search..."
              value={query}
              onChange={(event) => setQuery(event.target.value)}
              onKeyDown={(event) => {
                if (event.key === "Escape") setOpen(false);
                if (event.key === "Enter") {
                  const first = visibleOptions.find(
                    (option) => !option.disabled,
                  );
                  if (first) select(first);
                }
              }}
            />
          )}
          <ul className="fmp-select-list">
            {visibleOptions.length === 0 ? (
              <li className="fmp-select-empty">{emptyText}</li>
            ) : (
              visibleOptions.map((option) => (
                <li
                  key={option.value}
                  role="option"
                  aria-selected={option.value === value}
                  className={`fmp-select-option ${
                    option.value === value ? "is-selected" : ""
                  } ${option.disabled ? "is-disabled" : ""}`}
                  onClick={() => select(option)}
                >
                  <span className="fmp-select-label">{option.label}</span>
                  {option.meta ? (
                    <span className="fmp-select-meta">{option.meta}</span>
                  ) : null}
                  {option.hint ? (
                    <span className="fmp-select-hint">{option.hint}</span>
                  ) : null}
                </li>
              ))
            )}
          </ul>
        </div>
      )}
    </div>
  );
};

const FieldMasterProjections = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { handleErrorResponse } = useErrorHandler();
  const formCardRef = useRef(null);

  const [transactions, setTransactions] = useState([]);
  const [isLoadingTransactions, setIsLoadingTransactions] = useState(true);
  const [tranCode, setTranCode] = useState(
    (searchParams.get("tran_code") || "").trim().toUpperCase(),
  );
  const [options, setOptions] = useState(null);
  const [mappings, setMappings] = useState([]);
  const [isLoadingDetails, setIsLoadingDetails] = useState(false);

  const [form, setForm] = useState(EMPTY_FORM);
  const [editingId, setEditingId] = useState(null);
  const [isSaving, setIsSaving] = useState(false);
  const [showErrors, setShowErrors] = useState(false);

  const [pendingAction, setPendingAction] = useState(null);
  const [isActing, setIsActing] = useState(false);

  const handleErrorRef = useRef(handleErrorResponse);
  handleErrorRef.current = handleErrorResponse;

  const reportError = useCallback((response, fallback) => {
    handleErrorRef.current(getProjectionErrorMessage(response, fallback));
  }, []);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setIsLoadingTransactions(true);
      try {
        const response = await fetchTransactions("?count=500&page=1");
        if (cancelled) return;
        if (response && Array.isArray(response.items)) {
          const list = response.items
            .filter(
              (item) =>
                item?.tran_code &&
                String(item.item_table_name || "").trim() !== "",
            )
            .sort((a, b) =>
              String(a.tran_code).localeCompare(String(b.tran_code)),
            );
          setTransactions(list);
        } else {
          reportError(response, "Failed to load transactions");
        }
      } catch (error) {
        if (!cancelled) displayToast("Failed to load transactions", "error");
      } finally {
        if (!cancelled) setIsLoadingTransactions(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [reportError]);

  const loadDetails = useCallback(
    async (code, { silent = false } = {}) => {
      if (!code) {
        setOptions(null);
        setMappings([]);
        return;
      }
      if (!silent) setIsLoadingDetails(true);
      try {
        const [optionsResponse, mappingsResponse] = await Promise.all([
          fetchProjectionOptions(code),
          fetchProjections(code),
        ]);
        if (
          optionsResponse &&
          Array.isArray(optionsResponse.operations) &&
          !optionsResponse.error
        ) {
          setOptions({
            operations: optionsResponse.operations || [],
            source_item_fields: optionsResponse.source_item_fields || [],
            target_head_fields: optionsResponse.target_head_fields || [],
          });
        } else {
          setOptions(null);
          reportError(optionsResponse, "Failed to load projection options");
        }
        if (Array.isArray(mappingsResponse)) {
          setMappings(mappingsResponse);
        } else {
          setMappings([]);
          reportError(mappingsResponse, "Failed to load projections");
        }
      } catch (error) {
        setOptions(null);
        setMappings([]);
        displayToast("Failed to load projection details", "error");
      } finally {
        setIsLoadingDetails(false);
      }
    },
    [reportError],
  );

  useEffect(() => {
    setForm(EMPTY_FORM);
    setEditingId(null);
    setShowErrors(false);
    loadDetails(tranCode);
  }, [tranCode, loadDetails]);

  const handleTransactionChange = (code) => {
    const next = String(code || "").toUpperCase();
    setTranCode(next);
    setSearchParams(next ? { tran_code: next } : {}, { replace: true });
  };

  const selectedTransaction = useMemo(
    () => transactions.find((item) => item.tran_code === tranCode) || null,
    [transactions, tranCode],
  );

  const transactionOptions = useMemo(
    () =>
      transactions.map((item) => ({
        value: item.tran_code,
        label: item.tran_code,
        meta: item.name || "",
        keywords: `${item.head_table_name || ""} ${item.item_table_name || ""}`,
      })),
    [transactions],
  );

  const operationMeta = useMemo(
    () => getOperationMeta(form.operation, options?.operations || []),
    [form.operation, options],
  );
  const requiresSource = operationMeta ? operationMeta.requiresSource : false;

  const operationOptions = useMemo(() => {
    const serverOps = options?.operations || [];
    const ordered = [...PROJECTION_OPERATION_ORDER];
    serverOps.forEach((item) => {
      if (!ordered.includes(item.value)) ordered.push(item.value);
    });
    return ordered
      .map((value) => getOperationMeta(value, serverOps))
      .filter(Boolean)
      .map((meta) => ({
        value: meta.value,
        label: meta.label,
        meta: meta.value,
        hint: meta.description,
      }));
  }, [options]);

  const sourceFields = options?.source_item_fields || EMPTY_LIST;
  const targetFields = options?.target_head_fields || EMPTY_LIST;

  const selectedSource = useMemo(
    () =>
      sourceFields.find((field) => String(field.id) === form.sourceFieldId) ||
      null,
    [sourceFields, form.sourceFieldId],
  );

  const sourceOptions = useMemo(
    () =>
      sourceFields
        .filter(
          (field) =>
            !form.operation ||
            (field.allowed_operations || []).includes(form.operation),
        )
        .map((field) => ({
          value: String(field.id),
          label: field.field_name,
          meta: `${field.title || ""} · ${formatFieldType(field)}`,
          keywords: field.title || "",
        })),
    [sourceFields, form.operation],
  );

  const activeTargetIds = useMemo(() => {
    const ids = new Set();
    mappings.forEach((row) => {
      if (row?.mapping?.is_active && row.mapping.id !== editingId) {
        ids.add(row.mapping.target_head_field_id);
      }
    });
    return ids;
  }, [mappings, editingId]);

  const isTargetCompatible = useCallback(
    (field) => {
      if (!form.operation) return true;
      if (!(field.allowed_operations || []).includes(form.operation)) {
        return false;
      }
      if (form.operation === "SUM" && selectedSource) {
        return isCompatibleSumTarget(
          selectedSource.data_type,
          field.data_type,
        );
      }
      if (form.operation === "COPY_ONE" && selectedSource) {
        return Number(field.field_length) >= Number(selectedSource.field_length);
      }
      return true;
    },
    [form.operation, selectedSource],
  );

  const targetOptions = useMemo(
    () =>
      targetFields
        .filter((field) => isTargetCompatible(field))
        .map((field) => ({
          value: String(field.id),
          label: field.field_name,
          meta: `${field.title || ""} · ${formatFieldType(field)}`,
          keywords: field.title || "",
          disabled: activeTargetIds.has(field.id),
          hint: activeTargetIds.has(field.id) ? "Already mapped" : "",
        })),
    [targetFields, isTargetCompatible, activeTargetIds],
  );

  const selectedTarget = useMemo(
    () =>
      targetFields.find((field) => String(field.id) === form.targetFieldId) ||
      null,
    [targetFields, form.targetFieldId],
  );

  const newFieldDataTypeOptions = useMemo(() => {
    const base = PROJECTION_OPERATIONS[form.operation];
    if (!base) return [];
    return base.newFieldDataTypes
      .filter((type) =>
        form.operation === "SUM" && selectedSource
          ? isCompatibleSumTarget(selectedSource.data_type, type)
          : true,
      )
      .map((type) => ({
        value: type,
        label: PROJECTION_DATA_TYPE_LABELS[type] || type,
        meta: type,
      }));
  }, [form.operation, selectedSource]);

  const existingFieldNames = useMemo(() => {
    const names = new Set();
    [...sourceFields, ...targetFields].forEach((field) =>
      names.add(String(field.field_name || "").toLowerCase()),
    );
    return names;
  }, [sourceFields, targetFields]);

  const validationErrors = useMemo(() => {
    const errors = [];
    if (!tranCode) {
      errors.push("Select a transaction first.");
      return errors;
    }
    if (!form.operation) {
      errors.push("Select an operation.");
      return errors;
    }
    if (requiresSource && !selectedSource) {
      errors.push(`${operationMeta?.label} requires a source item field.`);
    }
    if (form.targetMode === "existing") {
      if (!selectedTarget) {
        errors.push("Select a target head field.");
      } else {
        if (activeTargetIds.has(selectedTarget.id)) {
          errors.push(
            `Head field "${selectedTarget.field_name}" already has an active mapping.`,
          );
        }
        if (!isTargetCompatible(selectedTarget)) {
          errors.push(
            `Head field "${selectedTarget.field_name}" is not compatible with ${operationMeta?.label}.`,
          );
        }
      }
    } else {
      const field = form.newField;
      const name = String(field.field_name || "").trim();
      if (!name) {
        errors.push("New head field name is required.");
      } else if (!FIELD_NAME_PATTERN.test(name)) {
        errors.push(
          "Field name may contain only letters, digits and underscores and cannot start with a digit.",
        );
      } else if (name.length > FIELD_NAME_MAX_LENGTH) {
        errors.push(
          `Field name must be at most ${FIELD_NAME_MAX_LENGTH} characters.`,
        );
      } else if (existingFieldNames.has(name.toLowerCase())) {
        errors.push(`A field named "${name}" already exists in ${tranCode}.`);
      }
      const title = String(field.title || "").trim();
      if (!title) {
        errors.push("New head field display title is required.");
      } else if (title.length > FIELD_TITLE_MAX_LENGTH) {
        errors.push(
          `Display title must be at most ${FIELD_TITLE_MAX_LENGTH} characters.`,
        );
      }
      if (!field.data_type) {
        errors.push("Select a data type for the new head field.");
      } else if (
        form.operation === "SUM" &&
        selectedSource &&
        !isCompatibleSumTarget(selectedSource.data_type, field.data_type)
      ) {
        errors.push(
          `SUM of a ${selectedSource.data_type} source needs a decimal/float/double target.`,
        );
      }
      if (!field.field_type) {
        errors.push("Select a field type for the new head field.");
      }
      const length = Number(field.field_length);
      if (!field.field_length || Number.isNaN(length) || length <= 0) {
        errors.push("Field length must be greater than 0.");
      } else if (
        form.operation !== "COPY_ONE" &&
        length > NUMERIC_TARGET_MAX_LENGTH
      ) {
        errors.push(
          `Numeric target length must be between 1 and ${NUMERIC_TARGET_MAX_LENGTH}.`,
        );
      } else if (form.operation === "COPY_ONE") {
        if (length > COPY_ONE_TARGET_MAX_LENGTH) {
          errors.push(
            `COPY_ONE target length cannot exceed ${COPY_ONE_TARGET_MAX_LENGTH}.`,
          );
        }
        if (selectedSource && length < Number(selectedSource.field_length)) {
          errors.push(
            `COPY_ONE target length must be at least ${selectedSource.field_length} (source length).`,
          );
        }
      }
    }
    return errors;
  }, [
    tranCode,
    form,
    requiresSource,
    selectedSource,
    selectedTarget,
    operationMeta,
    activeTargetIds,
    isTargetCompatible,
    existingFieldNames,
  ]);

  const previewText = useMemo(
    () =>
      buildPreview({
        operation: form.operation,
        source: requiresSource ? selectedSource : null,
        targetName:
          form.targetMode === "existing"
            ? selectedTarget?.field_name
            : form.newField.field_name,
      }),
    [form, requiresSource, selectedSource, selectedTarget],
  );

  const updateForm = (patch) => {
    setForm((prev) => ({ ...prev, ...patch }));
  };

  const updateNewField = (patch) => {
    setForm((prev) => ({ ...prev, newField: { ...prev.newField, ...patch } }));
  };

  const handleOperationChange = (operation) => {
    const base = PROJECTION_OPERATIONS[operation];
    setForm((prev) => {
      const keepSource =
        base?.requiresSource &&
        sourceFields.some(
          (field) =>
            String(field.id) === prev.sourceFieldId &&
            (field.allowed_operations || []).includes(operation),
        );
      const keepTarget = targetFields.some(
        (field) =>
          String(field.id) === prev.targetFieldId &&
          (field.allowed_operations || []).includes(operation),
      );
      return {
        ...prev,
        operation,
        sourceFieldId: keepSource ? prev.sourceFieldId : "",
        targetFieldId: keepTarget ? prev.targetFieldId : "",
        newField: {
          ...prev.newField,
          data_type: base?.defaultDataType || "",
          field_type: base?.defaultFieldType || "",
          field_length:
            prev.newField.field_length || (base ? String(base.defaultLength) : ""),
        },
      };
    });
  };

  const handleSourceChange = (sourceFieldId) => {
    const source = sourceFields.find(
      (field) => String(field.id) === sourceFieldId,
    );
    setForm((prev) => {
      const next = { ...prev, sourceFieldId };
      if (prev.operation === "COPY_ONE" && source) {
        const minLength = Number(source.field_length) || 0;
        const currentLength = Number(prev.newField.field_length) || 0;
        next.newField = {
          ...prev.newField,
          field_length:
            currentLength < minLength ? String(minLength) : prev.newField.field_length,
        };
      }
      return next;
    });
  };

  const resetForm = () => {
    setForm(EMPTY_FORM);
    setEditingId(null);
    setShowErrors(false);
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setShowErrors(true);
    if (validationErrors.length > 0) {
      displayToast(validationErrors[0], "error");
      return;
    }
    setIsSaving(true);
    try {
      const sourceId = requiresSource ? Number(form.sourceFieldId) : null;
      let response;
      if (editingId) {
        response = await updateProjection(editingId, {
          source_item_field_id: sourceId,
          target_head_field_id: Number(form.targetFieldId),
          operation: form.operation,
          show_in_head_view: Boolean(form.showInHeadView),
        });
      } else {
        const payload = {
          transaction_code: tranCode,
          operation: form.operation,
          show_in_head_view: Boolean(form.showInHeadView),
        };
        if (sourceId) payload.source_item_field_id = sourceId;
        if (form.targetMode === "existing") {
          payload.target_head_field_id = Number(form.targetFieldId);
        } else {
          payload.new_head_field = {
            field_name: form.newField.field_name.trim(),
            title: form.newField.title.trim(),
            data_type: form.newField.data_type,
            field_type: form.newField.field_type,
            field_length: Number(form.newField.field_length),
          };
        }
        response = await createProjection(payload);
      }

      if (isProjectionError(response) || !response?.id) {
        reportError(
          response,
          editingId
            ? "Failed to update projection"
            : "Failed to create projection",
        );
        return;
      }

      displayToast(
        editingId
          ? "Projection updated successfully"
          : "Projection created successfully",
        "success",
      );
      resetForm();
      await loadDetails(tranCode, { silent: true });
    } catch (error) {
      displayToast("Failed to save projection", "error");
    } finally {
      setIsSaving(false);
    }
  };

  const handleEdit = (row) => {
    const mapping = row.mapping;
    setEditingId(mapping.id);
    setShowErrors(false);
    setForm({
      operation: mapping.operation,
      sourceFieldId: mapping.source_item_field_id
        ? String(mapping.source_item_field_id)
        : "",
      targetMode: "existing",
      targetFieldId: String(mapping.target_head_field_id),
      newField: EMPTY_NEW_FIELD,
      showInHeadView: Boolean(mapping.show_in_head_view),
    });
    formCardRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  const handleConfirmAction = async () => {
    if (!pendingAction) return;
    const { type, row } = pendingAction;
    const id = row.mapping.id;
    setIsActing(true);
    try {
      let response;
      if (type === "activate") response = await activateProjection(id);
      if (type === "deactivate") response = await deactivateProjection(id);
      if (type === "delete") response = await deleteProjection(id);

      const succeeded =
        !isProjectionError(response) &&
        (type === "delete"
          ? response?.status === "deleted"
          : typeof response?.is_active === "boolean");

      if (!succeeded) {
        reportError(response, `Failed to ${type} projection`);
        return;
      }

      displayToast(
        type === "delete"
          ? "Projection deleted successfully"
          : type === "activate"
            ? "Projection activated"
            : "Projection deactivated",
        "success",
      );
      if (editingId === id) resetForm();
      await loadDetails(tranCode, { silent: true });
    } catch (error) {
      displayToast(`Failed to ${type} projection`, "error");
    } finally {
      setIsActing(false);
      setPendingAction(null);
    }
  };

  const activeCount = mappings.filter((row) => row?.mapping?.is_active).length;

  const renderRulesCard = () => (
    <div className="mfg-card fmp-rules-card">
      <div className="mfg-card-header">
        <h3 className="mfg-card-title">
          <span className="mfg-card-title-icon">📘</span>
          Operation Rules
        </h3>
      </div>
      <div className="fmp-rules-list">
        {PROJECTION_OPERATION_ORDER.map((operation) => {
          const meta = getOperationMeta(operation, options?.operations || []);
          if (!meta) return null;
          return (
            <div
              key={operation}
              className={`fmp-rule ${form.operation === operation ? "is-active" : ""}`}
            >
              <div className="fmp-rule-head">
                <span className={`fmp-op-badge ${meta.badgeClass || ""}`}>
                  {meta.short || operation}
                </span>
                <span className="fmp-rule-title">{meta.label}</span>
              </div>
              <p className="fmp-rule-description">{meta.description}</p>
              <ul className="fmp-rule-points">
                {(meta.rules || []).map((rule) => (
                  <li key={rule}>{rule}</li>
                ))}
              </ul>
            </div>
          );
        })}
      </div>
      <div className="fmp-note">
        <strong>Note:</strong> Projected head fields become read-only on edit
        and are recalculated automatically whenever item rows change.
        Deactivating a mapping clears the projected values; activating
        backfills them for all existing transactions.
      </div>
    </div>
  );

  const renderTargetSection = () => (
    <>
      <div className="fmp-section-head">
        <span className="fmp-step-chip">3</span>
        <div>
          <div className="fmp-section-title">Target head field</div>
          <div className="fmp-section-subtitle">
            {editingId
              ? "Pick the head field that will store the projected value."
              : "Use an existing head field or create a new one in the same step."}
          </div>
        </div>
      </div>

      {!editingId && (
        <div className="fmp-segmented" role="tablist">
          <button
            type="button"
            role="tab"
            aria-selected={form.targetMode === "existing"}
            className={`fmp-segment ${form.targetMode === "existing" ? "is-active" : ""}`}
            onClick={() => updateForm({ targetMode: "existing" })}
          >
            Existing head field
          </button>
          <button
            type="button"
            role="tab"
            aria-selected={form.targetMode === "new"}
            className={`fmp-segment ${form.targetMode === "new" ? "is-active" : ""}`}
            onClick={() => updateForm({ targetMode: "new" })}
          >
            Create new head field
          </button>
        </div>
      )}

      {form.targetMode === "existing" ? (
        <div className="mfg-form-group">
          <label className="mfg-form-label">Target head field *</label>
          <SearchableSelect
            options={targetOptions}
            value={form.targetFieldId}
            onChange={(value) => updateForm({ targetFieldId: value })}
            placeholder={
              form.operation
                ? "Select target head field"
                : "Select an operation first"
            }
            disabled={!form.operation || !options}
            emptyText="No compatible head fields. Create a new one instead."
          />
          {form.operation === "COPY_ONE" && selectedSource ? (
            <span className="fmp-help">
              Only string/char head fields with length ≥{" "}
              {selectedSource.field_length} are listed.
            </span>
          ) : null}
        </div>
      ) : (
        <div className="fmp-new-field">
          <div className="mfg-form-grid fmp-form-grid">
            <div className="mfg-form-group">
              <label className="mfg-form-label">Field name *</label>
              <input
                type="text"
                className="mfg-form-input"
                placeholder="e.g. total_quantity"
                value={form.newField.field_name}
                maxLength={FIELD_NAME_MAX_LENGTH}
                onChange={(event) =>
                  updateNewField({
                    field_name: event.target.value
                      .replace(/\s+/g, "_")
                      .toLowerCase(),
                  })
                }
              />
            </div>
            <div className="mfg-form-group">
              <label className="mfg-form-label">Display title *</label>
              <input
                type="text"
                className="mfg-form-input"
                placeholder="e.g. Total Quantity"
                value={form.newField.title}
                maxLength={FIELD_TITLE_MAX_LENGTH}
                onChange={(event) =>
                  updateNewField({ title: event.target.value })
                }
              />
            </div>
            <div className="mfg-form-group">
              <label className="mfg-form-label">Data type *</label>
              <SearchableSelect
                options={newFieldDataTypeOptions}
                value={form.newField.data_type}
                onChange={(value) => updateNewField({ data_type: value })}
                placeholder={
                  form.operation ? "Select data type" : "Select an operation first"
                }
                disabled={!form.operation}
                searchable={false}
              />
            </div>
            <div className="mfg-form-group">
              <label className="mfg-form-label">Field type *</label>
              <SearchableSelect
                options={PROJECTION_FIELD_TYPES}
                value={form.newField.field_type}
                onChange={(value) => updateNewField({ field_type: value })}
                placeholder="Select field type"
                disabled={!form.operation}
                searchable={false}
              />
            </div>
            <div className="mfg-form-group">
              <label className="mfg-form-label">Field length *</label>
              <input
                type="number"
                min={1}
                max={
                  form.operation === "COPY_ONE"
                    ? COPY_ONE_TARGET_MAX_LENGTH
                    : NUMERIC_TARGET_MAX_LENGTH
                }
                className="mfg-form-input"
                placeholder="e.g. 18"
                value={form.newField.field_length}
                onChange={(event) =>
                  updateNewField({ field_length: event.target.value })
                }
              />
            </div>
          </div>
          <span className="fmp-help">
            The new field is created as a head field (not required, read-only on
            edit) and mapped in the same request.
          </span>
        </div>
      )}
    </>
  );

  return (
    <div className="mfg-page-container fmp-page">
      <div className="mfg-page-header">
        <div className="mfg-page-title-section">
          <h2 className="mfg-page-title">
            <span className="mfg-page-title-icon">🔗</span>
            Field Master Projection
          </h2>
          <p className="mfg-page-subtitle">
            Project item-level values (sum, count, first value) into head fields
            of a transaction
          </p>
        </div>
        <div className="mfg-header-actions">
          <button
            className="mfg-btn mfg-btn-outline"
            onClick={() => navigate("/field-master")}
          >
            ⚙️ Field Master
          </button>
          <button
            className="mfg-btn mfg-btn-outline"
            onClick={() => loadDetails(tranCode)}
            disabled={!tranCode || isLoadingDetails}
          >
            🔄 Refresh
          </button>
        </div>
      </div>

      <div className="fmp-steps">
        {[
          ["1", "Load transaction & fields"],
          ["2", "Choose operation & source"],
          ["3", "Pick or create target"],
          ["4", "Save & manage mappings"],
        ].map(([step, label]) => (
          <div key={step} className="fmp-step">
            <span className="fmp-step-chip">{step}</span>
            <span className="fmp-step-label">{label}</span>
          </div>
        ))}
      </div>

      <div className="mfg-card">
        <div className="fmp-section-head">
          <span className="fmp-step-chip">1</span>
          <div>
            <div className="fmp-section-title">Transaction</div>
            <div className="fmp-section-subtitle">
              Only transactions with an item table can have projections.
            </div>
          </div>
        </div>
        <div className="fmp-transaction-row">
          <div className="mfg-form-group fmp-transaction-select">
            <label className="mfg-form-label">Transaction code *</label>
            <SearchableSelect
              options={transactionOptions}
              value={tranCode}
              onChange={handleTransactionChange}
              placeholder={
                isLoadingTransactions
                  ? "Loading transactions..."
                  : "Select a transaction"
              }
              disabled={isLoadingTransactions}
              emptyText="No transactions with item tables found"
            />
          </div>
          {selectedTransaction && (
            <div className="fmp-transaction-meta">
              <div className="mfg-view-item">
                <div className="mfg-view-label">Name</div>
                <div className="mfg-view-value">
                  {selectedTransaction.name || "-"}
                </div>
              </div>
              <div className="mfg-view-item">
                <div className="mfg-view-label">Head table</div>
                <div className="mfg-view-value fmp-mono">
                  {selectedTransaction.head_table_name || "-"}
                </div>
              </div>
              <div className="mfg-view-item">
                <div className="mfg-view-label">Item table</div>
                <div className="mfg-view-value fmp-mono">
                  {selectedTransaction.item_table_name || "-"}
                </div>
              </div>
              <div className="mfg-view-item">
                <div className="mfg-view-label">Fields loaded</div>
                <div className="mfg-view-value">
                  {options
                    ? `${targetFields.length} head · ${sourceFields.length} item`
                    : "-"}
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {!tranCode ? (
        <div className="mfg-card">
          <div className="mfg-empty-state">
            <div className="mfg-empty-icon">🔗</div>
            <h4 className="mfg-empty-title">Select a transaction to begin</h4>
            <p className="mfg-empty-text">
              Projections let a head field be calculated from the item rows of
              each transaction.
            </p>
          </div>
        </div>
      ) : isLoadingDetails ? (
        <div className="mfg-card">
          <Loader />
        </div>
      ) : (
        <>
          <div className="fmp-layout">
            <div className="mfg-card fmp-form-card" ref={formCardRef}>
              <div className="mfg-card-header">
                <h3 className="mfg-card-title">
                  <span className="mfg-card-title-icon">
                    {editingId ? "✏️" : "➕"}
                  </span>
                  {editingId
                    ? `Edit Projection #${editingId}`
                    : "Create Projection"}
                </h3>
                {editingId ? (
                  <button
                    type="button"
                    className="mfg-btn mfg-btn-outline mfg-btn-sm"
                    onClick={resetForm}
                  >
                    ✕ Cancel edit
                  </button>
                ) : null}
              </div>

              <form onSubmit={handleSubmit} className="fmp-form" noValidate>
                <div className="fmp-section-head">
                  <span className="fmp-step-chip">2</span>
                  <div>
                    <div className="fmp-section-title">
                      Operation & source item field
                    </div>
                    <div className="fmp-section-subtitle">
                      Source options are limited to item fields that support the
                      chosen operation.
                    </div>
                  </div>
                </div>

                <div className="mfg-form-grid fmp-form-grid">
                  <div className="mfg-form-group">
                    <label className="mfg-form-label">Operation *</label>
                    <SearchableSelect
                      options={operationOptions}
                      value={form.operation}
                      onChange={handleOperationChange}
                      placeholder="Select operation"
                      disabled={!options}
                      searchable={false}
                    />
                    {operationMeta ? (
                      <span className="fmp-help">
                        {operationMeta.description}
                      </span>
                    ) : null}
                  </div>
                  <div className="mfg-form-group">
                    <label className="mfg-form-label">
                      Source item field {requiresSource ? "*" : ""}
                    </label>
                    <SearchableSelect
                      options={sourceOptions}
                      value={form.sourceFieldId}
                      onChange={handleSourceChange}
                      placeholder={
                        !form.operation
                          ? "Select an operation first"
                          : requiresSource
                            ? "Select source item field"
                            : "Not required for COUNT_ROWS"
                      }
                      disabled={!form.operation || !requiresSource}
                      emptyText="No item fields support this operation"
                    />
                    {selectedSource && requiresSource ? (
                      <span className="fmp-help">
                        {selectedSource.title} · {formatFieldType(selectedSource)}
                      </span>
                    ) : null}
                  </div>
                </div>

                {renderTargetSection()}

                <div className="fmp-section-head">
                  <span className="fmp-step-chip">4</span>
                  <div>
                    <div className="fmp-section-title">Visibility & preview</div>
                  </div>
                </div>

                <label className="fmp-checkbox">
                  <input
                    type="checkbox"
                    checked={Boolean(form.showInHeadView)}
                    onChange={(event) =>
                      updateForm({ showInHeadView: event.target.checked })
                    }
                  />
                  <span>
                    Show in head view
                    <small>
                      Adds the target field to the transaction list columns
                      (head_view_fields).
                    </small>
                  </span>
                </label>

                <div className="fmp-preview">
                  <div className="fmp-preview-label">Preview</div>
                  <code className="fmp-preview-code">{previewText}</code>
                </div>

                {showErrors && validationErrors.length > 0 ? (
                  <div className="fmp-errors">
                    <div className="fmp-errors-title">
                      ⚠️ Please fix the following
                    </div>
                    <ul>
                      {validationErrors.map((error) => (
                        <li key={error}>{error}</li>
                      ))}
                    </ul>
                  </div>
                ) : null}

                <div className="mfg-form-actions">
                  <button
                    type="button"
                    className="mfg-btn mfg-btn-outline"
                    onClick={resetForm}
                    disabled={isSaving}
                  >
                    {editingId ? "Cancel" : "Reset"}
                  </button>
                  <button
                    type="submit"
                    className="mfg-btn mfg-btn-primary"
                    disabled={isSaving || !options}
                  >
                    {isSaving
                      ? "Saving..."
                      : editingId
                        ? "💾 Update Projection"
                        : "💾 Save Projection"}
                  </button>
                </div>
              </form>
            </div>

            {renderRulesCard()}
          </div>

          <div className="mfg-card">
            <div className="mfg-card-header">
              <h3 className="mfg-card-title">
                <span className="mfg-card-title-icon">📋</span>
                Configured Mappings
                <span className="fmp-count-pill">
                  {activeCount} active / {mappings.length} total
                </span>
              </h3>
            </div>

            {mappings.length === 0 ? (
              <div className="mfg-empty-state">
                <div className="mfg-empty-icon">📋</div>
                <h4 className="mfg-empty-title">No projections yet</h4>
                <p className="mfg-empty-text">
                  Create the first projection for {tranCode} using the form
                  above.
                </p>
              </div>
            ) : (
              <div className="mfg-table-wrapper">
                <table className="mfg-table fmp-table">
                  <thead>
                    <tr>
                      <th>Target Head Field</th>
                      <th>Display Title</th>
                      <th>Type</th>
                      <th>Source Item Field</th>
                      <th>Mapping</th>
                      <th>Head View</th>
                      <th>Status</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {mappings.map((row) => {
                      const mapping = row.mapping || {};
                      const target = row.target_head_field || {};
                      const source = row.source_item_field || null;
                      const meta = getOperationMeta(
                        mapping.operation,
                        options?.operations || [],
                      );
                      const isEditing = editingId === mapping.id;
                      return (
                        <tr
                          key={mapping.id}
                          className={isEditing ? "fmp-row-editing" : ""}
                        >
                          <td>
                            <strong className="fmp-mono">
                              {target.field_name || `#${mapping.target_head_field_id}`}
                            </strong>
                          </td>
                          <td>{target.title || "-"}</td>
                          <td className="fmp-mono">
                            {formatFieldType(target) || "-"}
                          </td>
                          <td className="fmp-mono">
                            {source ? source.field_name : "—"}
                          </td>
                          <td>
                            <div className="fmp-mapping-cell">
                              <span
                                className={`fmp-op-badge ${meta?.badgeClass || ""}`}
                              >
                                {meta?.short || mapping.operation}
                              </span>
                              <span className="fmp-mono fmp-formula">
                                {formatMappingFormula(row)}
                              </span>
                            </div>
                          </td>
                          <td>
                            <span
                              className={`fmp-bool ${mapping.show_in_head_view ? "is-yes" : ""}`}
                            >
                              {mapping.show_in_head_view ? "✓ Shown" : "— Hidden"}
                            </span>
                          </td>
                          <td>
                            <span
                              className={`mfg-status-badge ${
                                mapping.is_active
                                  ? "mfg-status-active"
                                  : "mfg-status-inactive"
                              }`}
                            >
                              {mapping.is_active ? "Active" : "Inactive"}
                            </span>
                          </td>
                          <td>
                            <div className="mfg-table-actions">
                              <button
                                className="mfg-btn mfg-btn-warning mfg-btn-sm"
                                onClick={() => handleEdit(row)}
                              >
                                ✏️ <span className="mfg-btn-text">Edit</span>
                              </button>
                              {mapping.is_active ? (
                                <button
                                  className="mfg-btn mfg-btn-secondary mfg-btn-sm"
                                  onClick={() =>
                                    setPendingAction({ type: "deactivate", row })
                                  }
                                >
                                  ⏸️ <span className="mfg-btn-text">Deactivate</span>
                                </button>
                              ) : (
                                <button
                                  className="mfg-btn mfg-btn-success mfg-btn-sm"
                                  onClick={() =>
                                    setPendingAction({ type: "activate", row })
                                  }
                                >
                                  ▶️ <span className="mfg-btn-text">Activate</span>
                                </button>
                              )}
                              <button
                                className="mfg-btn mfg-btn-danger mfg-btn-sm"
                                onClick={() =>
                                  setPendingAction({ type: "delete", row })
                                }
                              >
                                🗑️ <span className="mfg-btn-text">Delete</span>
                              </button>
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </>
      )}

      {pendingAction && (
        <div
          className="mfg-modal-overlay"
          onClick={() => !isActing && setPendingAction(null)}
        >
          <div className="mfg-modal" onClick={(event) => event.stopPropagation()}>
            <div className="mfg-modal-header">
              <h3 className="mfg-modal-title">
                <span>
                  {pendingAction.type === "delete"
                    ? "⚠️"
                    : pendingAction.type === "activate"
                      ? "▶️"
                      : "⏸️"}
                </span>
                {pendingAction.type === "delete"
                  ? "Confirm Delete"
                  : pendingAction.type === "activate"
                    ? "Activate Projection"
                    : "Deactivate Projection"}
              </h3>
              <button
                className="mfg-modal-close"
                onClick={() => setPendingAction(null)}
                disabled={isActing}
              >
                ✕
              </button>
            </div>
            <div className="mfg-modal-body">
              <p className="mfg-modal-confirm-text">
                {pendingAction.type === "delete"
                  ? "Are you sure you want to delete this projection mapping?"
                  : pendingAction.type === "activate"
                    ? "Activating will recalculate and backfill the target head field for all existing transactions."
                    : "Deactivating will stop recalculation and clear the projected values stored in the target head field."}
              </p>
              <div className="mfg-view-grid">
                <div className="mfg-view-item">
                  <div className="mfg-view-label">Transaction</div>
                  <div className="mfg-view-value">
                    {pendingAction.row.mapping.transaction_code}
                  </div>
                </div>
                <div className="mfg-view-item">
                  <div className="mfg-view-label">Mapping</div>
                  <div className="mfg-view-value fmp-mono">
                    {pendingAction.row.target_head_field?.field_name || "-"} ={" "}
                    {formatMappingFormula(pendingAction.row)}
                  </div>
                </div>
              </div>
              {pendingAction.type === "delete" ? (
                <p className="mfg-modal-danger-text-mt">
                  ⚠️ This action cannot be undone. Projected values in the head
                  table will be cleared.
                </p>
              ) : null}
            </div>
            <div className="mfg-modal-footer">
              <button
                className="mfg-btn mfg-btn-outline"
                onClick={() => setPendingAction(null)}
                disabled={isActing}
              >
                Cancel
              </button>
              <button
                className={`mfg-btn ${
                  pendingAction.type === "delete"
                    ? "mfg-btn-danger"
                    : pendingAction.type === "activate"
                      ? "mfg-btn-success"
                      : "mfg-btn-secondary"
                }`}
                onClick={handleConfirmAction}
                disabled={isActing}
              >
                {isActing
                  ? "Working..."
                  : pendingAction.type === "delete"
                    ? "🗑️ Delete"
                    : pendingAction.type === "activate"
                      ? "▶️ Activate"
                      : "⏸️ Deactivate"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default FieldMasterProjections;
